import { getProductTemplateBlotterColumn } from '@src/components/OTC/ProductTemplateTooltip';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import type { OTCDealData } from '@src/types/OTC';
import { getColumnTitle, getColumnTooltip } from '@src/utils/fieldDefinitionUtils';
import {
  formatOtcDealBlotterLocalDateTime,
  formatOtcDealBookingTimezoneTooltip,
  getOtcDealQuantityDisplayValue,
  getOtcDealSubmitTimeValue,
  getOtcDealUnitNotionalSizeValue,
} from '@src/utils/OTC/otcDealDisplayUtils';
import {
  appendTooltipWhenFeatureDisabled,
  PHYSICAL_SETTLEMENT_SUPPORT_UPCOMING,
} from '@src/utils/OTC/otcFeatureFlagTooltipAppend';
import { otcForwardFieldDefinitions } from '@src/utils/OTC/otcForwardFieldDefinitions';
import { otcOptionFieldDefinitions } from '@src/utils/OTC/otcOptionFieldDefinitions';
import { formatHolidayCalendars, formatProductType, formatSettlementCycle } from '@src/utils/OTC/otcUtils';
import {
  useDefaultColumns,
  type Column,
  type ColumnDef,
  type Leaves,
  type OTCForwardProductTemplate,
  type OTCOptionProductTemplate,
} from '@talos/kyoko';
import type { ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { otcDealUserColumn } from '../otcDealUserColumn';

function getVisibleColumns(): Array<keyof ReturnType<typeof useOTCDerivativesColumnDefs>> {
  return [
    'submitTime',
    'productType',
    'tradeDate',
    'effectiveDate',
    'status',
    'symbol',
    'productTemplate',
    'underlier',
    'quoteAsset',
    'tenor',
    'expirationDate',
    'settlementDate',
    'counterparty',
    'marketAccount',
    'side',
    'unitNotional',
    'notionalAmount',
    'price',
    'strikeType',
    'payoffType',
    'settlementType',
    'subAccount',
    'commission',
    'commissionCurrency',
    'postedMargin',
    'deliveryAsset',
    'user',
    'settlementCycle',
    'dealId',
    'businessDayConvention',
    'holidayCalendars',
    'index',
    'timestamp',
  ];
}

export const useOTCDerivativesColumns = (): Column[] => {
  const mapping = useOTCDerivativesColumnDefs();

  const columnDefinitions = useMemo(() => {
    return new Map<string, Column>(
      Object.entries(mapping)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, { ...value, id: key }])
    );
  }, [mapping]);

  const visibleColumns = useMemo(() => {
    return getVisibleColumns().filter(item => !!columnDefinitions.get(item));
  }, [columnDefinitions]);

  return useDefaultColumns(visibleColumns, columnDefinitions);
};

export function useOTCDerivativesColumnDefs() {
  const { enableOTCPhysicalSettlement } = useFeatureFlag();

  return useMemo(() => {
    const mapping = {
      submitTime: {
        field: 'SubmitTime',
        title: 'Submit Time',
        type: 'date',
        params: {
          milliseconds: true,
          valueGetter: (params: ValueGetterParams<OTCDealData>) => getOtcDealSubmitTimeValue(params.data),
        },
        initialSort: '-',
        initialWidth: 150,
        initialSortIndex: 0,
        description: 'Timestamp of the Deal Submission',
      },
      productType: {
        field: 'SubProductType',
        title: 'Product Type',
        type: 'text',
        initialWidth: 120,
        description: 'Type of OTC Derivatives product',
        params: {
          getLabel: formatProductType,
        },
      },
      tradeDate: {
        field: 'DealInfo.TradeTime',
        title: getColumnTitle(otcForwardFieldDefinitions.tradeDate),
        type: 'date',
        description: getColumnTooltip(otcForwardFieldDefinitions.tradeDate),
        initialWidth: 150,
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      effectiveDate: {
        field: 'DealInfo.EffectiveTime',
        title: getColumnTitle(otcForwardFieldDefinitions.effectiveDate),
        type: 'date',
        initialWidth: 150,
        description: getColumnTooltip(otcForwardFieldDefinitions.effectiveDate),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      symbol: {
        field: 'Symbol',
        title: 'Symbol',
        description: 'Resolved OTC Deal Symbol based on Product Template and Expiration Date',
        type: 'security',
        initialWidth: 200,
      },
      status: {
        field: 'Status',
        type: 'text',
        initialWidth: 80,
      },
      underlier: {
        field: 'Symbol',
        title: getColumnTitle(otcForwardFieldDefinitions.underlying),
        type: 'currency',
        initialWidth: 100,
        description: getColumnTooltip(otcForwardFieldDefinitions.underlying),
        params: {
          showDescription: false,
          symbolField: 'Symbol',
          securityCurrencyField: 'ProductDetails.BaseAsset',
        },
      },
      quoteAsset: {
        field: 'Symbol',
        title: getColumnTitle(otcForwardFieldDefinitions.quoteAsset),
        type: 'currency',
        initialWidth: 100,
        description: getColumnTooltip(otcForwardFieldDefinitions.quoteAsset),
        params: {
          showDescription: false,
          symbolField: 'Symbol',
          securityCurrencyField: 'ProductDetails.QuoteAsset',
        },
      },
      tenor: {
        field: 'Symbol',
        title: getColumnTitle(otcForwardFieldDefinitions.tenor),
        type: 'securityText',
        initialWidth: 80,
        description: getColumnTooltip(otcForwardFieldDefinitions.tenor),
        params: {
          symbolField: 'Symbol',
          securityField: 'ProductDetails.Tenor',
        },
      },
      expirationDate: {
        field: 'DealInfo.ExpiryTime',
        type: 'date',
        title: getColumnTitle(otcForwardFieldDefinitions.expiryTime),
        description: getColumnTooltip(otcForwardFieldDefinitions.expiryTime),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      settlementDate: {
        field: 'DealInfo.SettleTime',
        type: 'date',
        title: getColumnTitle(otcForwardFieldDefinitions.settleTime),
        description: getColumnTooltip(otcForwardFieldDefinitions.settleTime),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      productTemplate: getProductTemplateBlotterColumn({
        description: 'Product Template Symbol used to generate the deal',
      }),

      counterparty: {
        field: 'Counterparty',
        type: 'counterparty',
        title: getColumnTitle(otcForwardFieldDefinitions.counterparty),
        initialWidth: 120,
        description: getColumnTooltip(otcForwardFieldDefinitions.counterparty),
      },
      side: {
        field: 'DealSide',
        title: getColumnTitle(otcForwardFieldDefinitions.side),
        type: 'side',
        description: getColumnTooltip(otcForwardFieldDefinitions.side),
      },
      unitNotional: {
        field: 'Symbol',
        title: getColumnTitle(otcForwardFieldDefinitions.unitNotional),
        type: 'size',
        initialWidth: 100,
        description: getColumnTooltip(otcForwardFieldDefinitions.unitNotional),
        params: {
          valueGetter: getOtcDealUnitNotionalSizeValue,
        },
      },
      notionalAmount: {
        field: 'DealInfo.NotionalAmount',
        title: getColumnTitle(otcForwardFieldDefinitions.notionalAmount),
        type: 'size',
        initialWidth: 120,
        description: getColumnTooltip(otcForwardFieldDefinitions.notionalAmount),
        params: { currencyField: 'DealInfo.NotionalAsset' },
      },
      price: {
        id: 'price',
        title: getColumnTitle(otcForwardFieldDefinitions.strikeFwdPrice),
        type: 'strikeFwdPrice',
        description: getColumnTooltip(otcForwardFieldDefinitions.strikeFwdPrice),
      },
      strikeType: {
        field: 'Symbol',
        title: getColumnTitle(otcOptionFieldDefinitions.strikeType),
        type: 'securityText',
        initialWidth: 100,
        description: getColumnTooltip(otcOptionFieldDefinitions.strikeType),
        params: {
          symbolField: 'Symbol',
          securityField: 'ProductDetails.StrikeType' satisfies Leaves<OTCOptionProductTemplate>,
        },
      },
      payoffType: {
        field: 'Symbol',
        title: getColumnTitle(otcForwardFieldDefinitions.payoffType),
        type: 'securityText',
        initialWidth: 120,
        description: getColumnTooltip(otcForwardFieldDefinitions.payoffType),
        params: {
          symbolField: 'Symbol',
          securityField: 'ProductDetails.PayoffType' satisfies Leaves<OTCForwardProductTemplate>,
        },
      },
      settlementType: {
        field: 'Symbol',
        title: getColumnTitle(otcForwardFieldDefinitions.settlementType),
        type: 'securityText',
        initialWidth: 120,
        description: appendTooltipWhenFeatureDisabled(
          getColumnTooltip(otcForwardFieldDefinitions.settlementType),
          enableOTCPhysicalSettlement,
          PHYSICAL_SETTLEMENT_SUPPORT_UPCOMING
        ),
        params: {
          symbolField: 'Symbol',
          securityField: 'ProductDetails.SettlementType' satisfies Leaves<OTCForwardProductTemplate>,
        },
      },
      subAccount: {
        field: 'SubAccount',
        type: 'subAccount',
        title: 'Sub Account(s)',
        initialWidth: 150,
      },
      commission: {
        field: 'DealInfo.CommissionBps',
        title: getColumnTitle(otcForwardFieldDefinitions.commission),
        type: 'size',
        params: {
          currency: 'BPS',
        },
        initialWidth: 120,
        description: getColumnTooltip(otcForwardFieldDefinitions.commission),
      },
      commissionCurrency: {
        field: 'DealInfo.CommissionCurrency',
        title: getColumnTitle(otcForwardFieldDefinitions.commissionCurrency),
        type: 'currency',
        params: {
          showDescription: false,
        },
        description: getColumnTooltip(otcForwardFieldDefinitions.commissionCurrency),
      },
      postedMargin: {
        field: 'DealInfo.InitialMarginAmount',
        title: getColumnTitle(otcForwardFieldDefinitions.postedMargin),
        type: 'size',
        description: getColumnTooltip(otcForwardFieldDefinitions.postedMargin),
        params: {
          currencyField: 'DealInfo.InitialMarginAsset',
        },
      },
      deliveryAsset: {
        field: 'Symbol',
        title: getColumnTitle(otcForwardFieldDefinitions.deliveryAsset),
        type: 'currency',
        description: getColumnTooltip(otcForwardFieldDefinitions.deliveryAsset),
        params: {
          showDescription: false,
          symbolField: 'Symbol',
          securityCurrencyField: 'ProductDetails.DeliveryAsset' satisfies Leaves<OTCForwardProductTemplate>,
        },
      },
      user: otcDealUserColumn,
      settlementCycle: {
        field: 'DealInfo.SettleCycle',
        title: getColumnTitle(otcForwardFieldDefinitions.settlementCycle),
        type: 'text',
        description: getColumnTooltip(otcForwardFieldDefinitions.settlementCycle),
        params: {
          getLabel: formatSettlementCycle,
        },
      },
      dealId: {
        field: 'ID',
        type: 'id',
        initialWidth: 100,
        title: 'Deal ID',
        description: 'The unique Talos identifier for the OTC derivative deal',
      },
      businessDayConvention: {
        field: 'DealInfo.BusinessDayHolidayConvention',
        title: getColumnTitle(otcForwardFieldDefinitions.businessDayConvention),
        type: 'text',
        description: getColumnTooltip(otcForwardFieldDefinitions.businessDayConvention),
      },
      holidayCalendars: {
        field: 'DealInfo.HolidayCalendars',
        title: getColumnTitle(otcForwardFieldDefinitions.holidayCalendars),
        type: 'text',
        description: getColumnTooltip(otcForwardFieldDefinitions.holidayCalendars),
        params: {
          getLabel: formatHolidayCalendars,
        },
      },
      index: {
        field: 'Symbol',
        title: getColumnTitle(otcForwardFieldDefinitions.index),
        type: 'securityText',
        description: getColumnTooltip(otcForwardFieldDefinitions.index),
        params: {
          symbolField: 'Symbol',
          securityField: 'ProductDetails.Index' satisfies Leaves<OTCForwardProductTemplate>,
        },
      },
      quantity: {
        field: 'Quantity',
        type: 'size',
        title: getColumnTitle(otcForwardFieldDefinitions.quantity),
        description: getColumnTooltip(otcForwardFieldDefinitions.quantity),
        params: {
          valueGetter: (params: ValueGetterParams<OTCDealData>) => {
            const data = params.data;
            if (!data) {
              return { value: '', currency: '' };
            }
            const display = getOtcDealQuantityDisplayValue(data);
            return { value: display.value, currency: display.currency };
          },
        },
      },

      quantityAsset: {
        field: 'QuantityAsset',
        title: 'Quantity Asset',
        type: 'currency',
        params: {
          showDescription: false,
        },
      },
      marketAccount: {
        field: 'MarketAccount',
        title: 'Market Account',
        type: 'marketAccount',
      },
      externalId: {
        field: 'ExternalID',
        title: 'External ID',
        type: 'text',
      },
      timestamp: {
        field: 'Timestamp',
        title: 'Updated At',
        type: 'date',
        params: { milliseconds: true },
      },
      revision: {
        field: 'Revision',
        type: 'number',
      },
    } as const satisfies Record<string, ColumnDef<OTCDealData>>;

    return mapping;
  }, [enableOTCPhysicalSettlement]);
}
